import type { SimpleOfferResponse, TokenInfo } from '@yoop/server-model/token-info';
import { OfferSubStatus } from '@yoop/server-model/token-info';
import type { ReceiptData, ReceiptOfferData, TokenChoice } from '@yoop/whitelabel-data';
import { type ReceiptUpgradeData, TokenDeliveryMethod } from '@yoop/whitelabel-data';
import type { CheckoutSeats, CustomerOrderReceiptResponse } from '@yoop/data-access-model';
import { type CategoryDetailResponse, CategoryType } from '@yoop/server-model/inventory-category';
import { isNilOrEmpty } from '@yoop/util';
import { SeatConfigurationType } from '@yoop/server-model/seating';
import { deliveryMethodFrom, tokenChoicesFrom, tokenFeeFrom } from '@yoop/whitelabel-token';
import type { UpgradeDirectionSummary } from '@yoop/util-offer/helper';
import { getOfferUpgradeMap } from '@yoop/util-offer/helper';
import { owned } from '@yoop/util-token/predicate';
import { datetime } from '@yoop/util-datetime';
import type { Currency } from '@yoop/server-model/payment';
import { getTokenType } from '@yoop/util-token/helper';
import { creditCardFrom, paymentMethodFrom } from '@yoop/whitelabel-payment';
import { joinNonNull } from '@yoop/util-string';

type CategoryDetailMap = Record<
  number,
  Pick<CategoryDetailResponse, 'type' | 'choiceSets' | 'merchandiseDeliveryMethod'>
>;

export const receiptDataModelFrom = (
  receipt: CustomerOrderReceiptResponse,
  currency: Currency,
  categoryDetailMap: CategoryDetailMap,
): ReceiptData => {
  const inventoryUpgradeData = getOfferUpgradeMap(receipt);
  const tokens = receipt.tokenInformation
    .map((token): TokenInfo => ({ ...token, type: getTokenType(token), owned: true }))
    .filter(canSeeReceipt);

  const offers =
    receipt.offerResponseList?.map(
      (offer): ReceiptOfferData => ({
        ...getSeatsAndMerchDetailsForOfferData(offer, tokens, categoryDetailMap),
        tokenIds: tokens
          .filter((token) => token.offerResponse.id === offer.id)
          .map((token) => token.tokenId),
        amountPerToken: parseFloat(offer.tokenPrice.amountPerToken),
        resaleText: offer.checkoutResaleText,
        id: offer.id,
        numberOfTokens: offer.numberOfTokens,
        description: offer.offerGroupDescription,
        upgrades: offerUpgradesDataFrom({
          upgradedFrom: inventoryUpgradeData[offer.id]?.from,
          upgradedTo: inventoryUpgradeData[offer.id]?.to,
        }),
      }),
    ) ?? [];

  return {
    orderId: receipt.customerOrderId.toString(),
    currency,
    payment: receipt.paymentMeans && {
      method: paymentMethodFrom(receipt.paymentMeans.type),
      cardType: creditCardFrom(receipt.paymentMeans.cardType),
      lastDigits: receipt.paymentMeans.lastDigits,
    },
    fees: receipt.overallResponse.feeBreakdown?.map(tokenFeeFrom) ?? [],
    offers,
    purchaseDatetime: datetime.parse(receipt.purchaseTimestamp).toISOString(),
    termsText: receipt.checkoutTermsAndConditions?.checkoutTerms,
    shippingAddress: joinNonNull(
      [receipt.shippingAddress?.freeFormAddress, receipt.shippingAddress?.unitInformation],
      ', ',
    ),
    totalAmount: parseFloat(receipt.overallResponse.totalAmount),
    tokenAmount: parseFloat(receipt.overallResponse.totalTokenAmount),
  };
};
// Receipt data model helpers
const canSeeReceipt = (token: TokenInfo): boolean =>
  owned(token) &&
  token.offerResponse?.offerSubStatus !== OfferSubStatus.REFUNDED &&
  parseFloat(token.offerResponse?.tokenPrice?.totalAmount ?? '0') > 0;

const getSeatsAndMerchDetailsForOfferData = (
  offer: SimpleOfferResponse,
  tokens: TokenInfo[],
  categoryDetailMap: CategoryDetailMap,
): Pick<ReceiptOfferData, 'seats' | 'choices' | 'deliveryMethod'> => {
  const tokensForOffer = tokens.filter((token) => token.offerResponse.id === offer.id);
  let seats: CheckoutSeats;
  let choices: TokenChoice[];
  let deliveryMethod: TokenDeliveryMethod;
  let categoryType: CategoryType;

  if (!isNilOrEmpty(tokensForOffer)) {
    const referenceToken = tokensForOffer[0];
    seats =
      referenceToken.seatConfigurationType === SeatConfigurationType.SEATED
        ? {
            sectionName: referenceToken.inventoryItemSeatingData.section.name,
            rowName: referenceToken.inventoryItemSeatingData.row.name,
            seats: tokensForOffer.map((token): string => token.inventoryItemSeatingData.seat.name),
          }
        : getSeatDetailsForGA(
            referenceToken.categoryName,
            referenceToken.inventoryItemSeatingData.section.name,
          );
    const categoryDetail = categoryDetailMap[referenceToken.categoryId];
    choices = tokenChoicesFrom(
      referenceToken.offerResponse.selectedChoiceResponse,
      categoryDetail?.choiceSets,
    );

    deliveryMethod = deliveryMethodFrom(categoryDetail?.merchandiseDeliveryMethod);
    categoryType = categoryDetail?.type;
  }
  return {
    seats,
    choices,
    deliveryMethod:
      categoryType === CategoryType.EVENT_ACCESS ? TokenDeliveryMethod.Digital : deliveryMethod,
  };
};

const getSeatDetailsForGA = (categoryName: string, sectionName: string) => {
  return categoryName !== sectionName ? { sectionName: sectionName } : null;
};

const offerUpgradesDataFrom = ({
  upgradedTo,
  upgradedFrom,
}: {
  upgradedFrom: UpgradeDirectionSummary;
  upgradedTo: UpgradeDirectionSummary;
}): ReceiptUpgradeData[] => {
  const upgrades: ReceiptUpgradeData[] = [];
  Object.entries(upgradedFrom ?? {}).forEach(([otherReceiptId, { date, count }]) => {
    upgrades.push({
      date: date.toISOString(),
      otherReceiptId,
      count,
      wasUpgradedTo: false,
    });
  });
  Object.entries(upgradedTo ?? {}).forEach(([otherReceiptId, { date, count }]) => {
    upgrades.push({
      date: date.toISOString(),
      otherReceiptId,
      count,
      wasUpgradedTo: true,
    });
  });
  return upgrades.sort((lhs, rhs) =>
    datetime.diff(datetime.parse(rhs.date), datetime.parse(lhs.date)),
  );
};
